/**
 * PermissionsContext — exposes the current user's collaborator permissions
 * so UI code can hide/disable elements per key.
 *
 * For non-COLLABORATOR roles, `has()` returns true for every key (the classic
 * role-based checks upstream take care of blocking access). This keeps
 * legacy screens unaffected.
 */
import React, { createContext, useContext, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";

const PermissionsContext = createContext({
  role: null,
  permissions: {},
  has: () => true,
  mode: () => "allowed",
});

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  const role = user?.role;

  const value = useMemo(() => {
    const permissions = user?.permissions || {};
    const isCollab = role === "COLLABORATOR";
    return {
      role,
      permissions,
      isCollab,
      // has(key) — true when the key is "allowed" or "approval". Non-collaborators
      // are unrestricted here; UI hides items only when explicitly denied.
      has: (key) => {
        if (!isCollab) return true;
        const m = permissions[key];
        return m === "allowed" || m === "approval";
      },
      // mode(key) — the raw mode string, defaults to "allowed" for non-collaborators.
      mode: (key) => {
        if (!isCollab) return "allowed";
        return permissions[key] || "denied";
      },
    };
  }, [role, user?.permissions]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
  return useContext(PermissionsContext);
}
